// React and hooks
import React, { useState, useEffect, useRef } from 'react';

// Redux
import { useDispatch, useSelector } from 'react-redux';

// Routing
import { NavLink } from 'react-router-dom';

// UI Components (from React Bootstrap)
import { Row, Col, Dropdown, Button, Card, Badge, OverlayTrigger, Tooltip } from 'react-bootstrap';
// Utils and error handlers
import moment from 'moment';
import { handlerErrors } from 'utils/handleErrors';

// Custom Components
import HtmlHead from 'components/html-head/HtmlHead';
import CsLineIcons from 'cs-line-icons/CsLineIcons';
// utils
import formatMoney from 'utils/formatMoney';
// components
import EmptyFilterResult from 'components/emptyStates/EmptyFilterResult';
import ConnectionError from 'components/emptyStates/ConnectionError';
import LoadingPulse from '../../../components/loading/LoadingPulse';
import LoadingCar from '../../../components/loading/LoadingCar';
import Paginate from '../../../components/Paginate';
import EmptyPage from '../../../components/emptyStates/EmptyPage';
import RainCalendar from '../../../components/calendars/RainCalendar';
import ProvisionalUserAlert from '../../../components/alerts/UserAlert';

// Redux Actions
import { setPage, setItemsPerPage } from '../../../slices/uiStates/userPluginOrdersSlice';
// API calls
import { getFilteredPluginOrders, getPluginShops, getPluginOrders } from '../../../api/Api-user';

const { FormatMoney } = require('format-money-js');

const PluginOrders = () => {
  const title = 'Pedidos';
  const description = 'Historial Pedidos Plataforma Autopaquete';
  // - Use references to do scroll Up
  const startRef = useRef(null);
  const dispatch = useDispatch();

  // - Global State from Store.js
  const { page, itemsPerPage } = useSelector((state) => state.user_plugin_orders);
  // -Local States
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [orders, setOrders] = useState([]);
  const [emptyHistory, setEmptyHistory] = useState(null);
  const [hasConnectionIssue, setHasConnectionIssue] = useState(false);
  // Start Pagination States
  const [totalPages, setTotalPages] = useState(null);
  const [pageView, setPageView] = useState(page); // solo para la vista
  const [itemsPerPageView, setItemsPerPageView] = useState(itemsPerPage); // solo para la vista
  const [totalItems, setTotalItems] = useState(0);
  // End Pagination States
  // Start Filter States
  const [filteredMode, setFilteredMode] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  // End Filter States
  // Start States for Components for Filtering
  const [selectStartDate, setSelectStartDate] = useState({ date: undefined });
  const [selectEndDate, setSelectEndDate] = useState({ date: undefined });
  const [filteredSuccess, setFilteredSuccess] = useState('');
  const [storeList, setStoreList] = useState([]);
  const [storeName, setStoreName] = useState('');
  const [orderState, setOrderState] = useState('');

  const fm = new FormatMoney({
    decimals: 0, // No decimales
  });
  // End States for Components for Filtering
  const resetInitialDate = () => {
    setSelectEndDate({ date: undefined });
    setSelectStartDate({ date: undefined });
    setStartDate('');
    setEndDate('');
  };
  const getStatusColor = (status) => {
    const color = {
      Pendiente: 'secondary',
      Completada: 'success',
      Incompleta: 'warning',
      Cancelada: 'alternate',
    };
    return color[status] || 'alternate';
  };
  // remove accents
  const filterInitialValues = () => {
    setStoreName('');
    setOrderState('');
    resetInitialDate();
    setFilteredSuccess(''); // para eliminar el mensaje "Filtro por nombre..."
  };
  //
  // - Get all Orders
  const handlerGetOrdersHistory = async () => {
    if (!isInitialLoading) {
      // solo si shipping es true [] activa este loader, si es falsy null usa el loader del car para los inicios
      setIsLoading(true);
    }
    try {
      const response = await getPluginOrders(page, itemsPerPage);
      // console.log('getPluginOrders response:', response);
      if (response.orders !== undefined) {
        // en cada respuesta actualizar el estado currentPage
        // para asegurarse de que es la pag esperada
        // Obtener lo que ya existe en localIntegrationsState
        const prev = JSON.parse(localStorage.getItem('localIntegrationsState')) || {};
        // Guardar la nueva data combinada
        const updated = {
          ...prev,
          ordersPlugin: response.orders,
        };
        localStorage.setItem('localIntegrationsState', JSON.stringify(updated));
        setOrders(response.orders);
        setTotalPages(response.total_pages); // para manejar la paginacion, si el valor es 1 no se muestra
        setTotalItems(response.total_items);
        setPageView(response.current_page); // para actualizar la vista
        setItemsPerPageView(response.page_size); // para actualizar la vista
        if (startRef.current && startRef.current.scrollIntoView) {
          startRef.current.scrollIntoView({ behavior: 'smooth' });
        }
        if (response.orders.length === 0) {
          setEmptyHistory(orders || true); // para mostrar mensaje de "No tienes historial"
        }
      }
    } catch (error) {
      setTotalPages(0);
      setFilteredSuccess(''); // para eliminar el mensaje "Filtro por servicio..."
      setHasConnectionIssue(true); // only show component if this is true and history list is empty
      handlerErrors(error);
    } finally {
      setIsLoading(false);
      setIsInitialLoading(false);
    }
  };
  // - Search shipping by filters
  const handlerGetFilteredOrders = async () => {
    // console.log('status:', orderState);
    try {
      setIsLoading(true);
      const response = await getFilteredPluginOrders(page, itemsPerPage, storeName, orderState, startDate, endDate);
      // console.log(' handlerGetFilteredOrders response:', response);
      if (response.orders !== undefined) {
        // Obtener lo que ya existe en localIntegrationsState
        const prev = JSON.parse(localStorage.getItem('localIntegrationsState')) || {};
        // Guardar la nueva data combinada
        const updated = {
          ...prev,
          ordersPlugin: response.orders,
        };
        localStorage.setItem('localIntegrationsState', JSON.stringify(updated));
        setEmptyHistory(false); // hide empty component when applying filters
        setOrders(response.orders);
        setTotalPages(response.total_pages);
        setTotalItems(response.total_items);
        setPageView(response.current_page); // actualizar la vista de la paginacion
        setItemsPerPageView(response.page_size); // para actualizar la vista
        setFilteredSuccess(
          `Filtro ${storeName ? `por tienda ▸ ${storeName}` : ''} ${orderState ? `por estado de pedido ▸ ${orderState}` : ''} 
            ${startDate ? ` ▸ ${moment(startDate).format('DD-MM-YY')}` : ''} ${endDate ? `al  ${moment(endDate).format('DD-MM-YY')}` : ''}`
        );
        if (startRef.current && startRef.current.scrollIntoView) {
          startRef.current.scrollIntoView({ behavior: 'smooth' });
        }
      }
    } catch (error) {
      setHasConnectionIssue(true);
      handlerErrors(error);
      setTotalPages(0);
      setFilteredSuccess(true); // para eliminar el mensaje "Filtro por servicio..."
    } finally {
      setIsLoading(false);
      setFilteredMode(true); // respuesta exitosa o no es necesario para mantener el modo filtrando y que aparezca el btn regresar
    }
  };
  const settingPage = (record) => {
    dispatch(setItemsPerPage(record)); // ejecuta el useEffect que decide que funcion ejecutar
    dispatch(setPage(1)); // ejecuta el useEffect que decide que funcion ejecutar
  };
  const returnInitialMode = () => {
    // handlerGetFilteredHistory() o  handlerGetOrdersHistory() activan el loader
    // setFilterBy('envío'); // ejecuta el useEffect que prepara los estados para el filtrado
    setFilteredMode(false);
    filterInitialValues();
    if (page > 1) {
      dispatch(setPage(1)); // ejecutara el useEffect que decide que fn ejecutar pero con la pag 1
    } else {
      // si changePage es 1 no ejecuta el cambio de estado ni el useEffect es necesario llamar a la funcion
      handlerGetOrdersHistory();
    }
  };
  // - Whenever the pagination status change
  useEffect(() => {
    const storedData = JSON.parse(localStorage.getItem('localIntegrationsState'));
    if (storedData?.ordersPlugin) {
      setOrders(storedData.ordersPlugin);
    }
    if (filteredMode) {
      handlerGetFilteredOrders();
    } else {
      handlerGetOrdersHistory();
    }
  }, [page, itemsPerPage]);

  useEffect(() => {
    const storedData = JSON.parse(localStorage.getItem('localIntegrationsState'));
    if (storedData?.stores) {
      setStoreList(storedData.stores);
    }
    getPluginShops().then((response) => {
      // console.log('res getPluginShops:', response.stores);
      if (response.stores !== undefined) {
        localStorage.setItem('stores', JSON.stringify(response.stores));
        setStoreList(response.stores);
        if (response.stores.length === 0) {
          setEmptyHistory(storedData.stores || true); // para mostrar mensaje de "No tienes historial"
        }
        // Obtener lo que ya existe en localIntegrationsState
        const prev = JSON.parse(localStorage.getItem('localIntegrationsState')) || {};
        // Guardar la nueva data combinada
        const updated = {
          ...prev,
          stores: response.stores,
        };
        localStorage.setItem('localIntegrationsState', JSON.stringify(updated));
      }
    });
  }, []);
  useEffect(() => {
    // console.log('🔵 Users component mountend');

    return () => {
      // dispatch(resetFilters());
      // console.log('🔴  Users component unmountend');
    };
  }, []);
  // console.log('items:', itemsPerPage, 'pedidos list:', orders);

  return (
    <>
      {isInitialLoading && <LoadingCar />}
      {isLoading && <LoadingPulse />}
      {/* pageContainer I use it to add opacity screen while loading data */}
      <div id="pageContainer">
        <HtmlHead title={title} description={description} />
        <div className="page-title-container" ref={startRef}>
          <Row className="g-0">
            {/* Title Start */}
            <Col className="col-auto mb-3 mb-sm-0 me-auto order-1">
              <NavLink className="muted-link pb-1 d-inline-block hidden breadcrumb-back" to="/">
                <CsLineIcons icon="chevron-left" size="13" />
                <span className="align-middle text-small ms-1">Inicio</span>
              </NavLink>
              <h1 className="mb-0 pb-0 display-4" id="title">
                {title}
              </h1>
            </Col>
            {/* Title End */}
            {/* Excel Buttons Start */}
            <Col sm="auto" className="d-flex align-items-end justify-content-end mb-2 mb-sm-0 order-3 order-sm-2">
              {/* {orders?.length > 0 && <ShippingCSV shippings={orders} crmId="" />} */}
            </Col>
            {/* Excel  Buttons End */}
          </Row>
        </div>
        <div style={{ minHeight: '50vh' }} id="cardContainer">
          {/* Elements for Filtering Start */}
          <Row className="g-0 pb-1 pb-lg-2">
            {/* Filters Start */}
            {/* Inputs filters Start */}
            <Col xs="12" xxl="10" className="mb-0 mb-xl-0 bg-infox">
              <Row className="g-0">
                <Col xs="6" sm="6" md="3" className="mb-2 mb-lg-0 pe-2 bg-successx">
                  <Dropdown className="d-inline-block w-100">
                    <Dropdown.Toggle variant="foreground-alternate" className="shadow d-flex align-items-center justify-content-between w-100">
                      {storeName || 'Tienda'}
                    </Dropdown.Toggle>
                    <Dropdown.Menu className="shadow sw-auto" style={{ minHeight: '100px' }}>
                      <Dropdown.Item onClick={() => setStoreName('')}>Ninguna</Dropdown.Item>
                      {storeList?.map((store) => (
                        <Dropdown.Item key={store} onClick={() => setStoreName(store.name)}>
                          {store.name}
                        </Dropdown.Item>
                      ))}
                    </Dropdown.Menu>
                  </Dropdown>
                </Col>
                <Col xs="6" sm="6" md="3" className="mb-2 mb-lg-0 pe-md-2">
                  <Dropdown className="d-inline-block w-100">
                    <Dropdown.Toggle variant="foreground-alternate" className="shadow d-flex align-items-center justify-content-between w-100">
                      {orderState || 'Estado'}
                    </Dropdown.Toggle>
                    <Dropdown.Menu className="shadow sw-auto" style={{ minHeight: '100px' }}>
                      <Dropdown.Item onClick={() => setOrderState('')}>Ninguno</Dropdown.Item>
                      <Dropdown.Item onClick={(e) => setOrderState(e.target.innerText)}>Incompleta</Dropdown.Item>
                      <Dropdown.Item onClick={(e) => setOrderState(e.target.innerText)}>Cancelada</Dropdown.Item>
                      <Dropdown.Item onClick={(e) => setOrderState(e.target.innerText)}>Pendiente</Dropdown.Item>
                      <Dropdown.Item onClick={(e) => setOrderState(e.target.innerText)}>Completada</Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>
                </Col>
                <Col xs="6" sm="6" md="3" className="mb-2 mb-lg-0 pe-2 bg-warningx">
                  <RainCalendar
                    selectDate={selectStartDate}
                    setSelectDate={setSelectStartDate}
                    setState={setStartDate}
                    state={startDate}
                    holder="Fecha inicial"
                    width="100px"
                  />
                </Col>
                <Col xs="6" sm="6" md="3" className="mb-2 mb-lg-0">
                  <RainCalendar selectDate={selectEndDate} setSelectDate={setSelectEndDate} setState={setEndDate} holder="Fecha final" width="100px" />
                </Col>
              </Row>
            </Col>
            {/* Inputs filters End */}
          </Row>
          {/* Filters End */}
          <Row className="g-0">
            <Col xs="12" sm="9">
              <Row className="g-0">
                <Col xs="auto" className="pe-2">
                  <Button
                    variant="primary"
                    style={{ height: '36px' }}
                    className="w-100"
                    onClick={() => {
                      if (page > 1) {
                        // la primera vez el estado filteredMode es falso
                        setFilteredMode(true); // para que el useEffect funcione bien
                        dispatch(setPage(1)); // ejecutara el useEffect que decide que fn ejecutar pero con la pag 1
                      } else {
                        handlerGetFilteredOrders();
                      }
                    }}
                  >
                    <CsLineIcons icon="search" size="15" className="mb-1 me-1" />
                    <span>Buscar</span>
                  </Button>
                </Col>
                {filteredMode && (
                  <Col xs="auto">
                    <Button variant="outline-primary" style={{ height: '36px' }} className="w-100" onClick={returnInitialMode}>
                      {/* <CsLineIcons icon="refresh-horizontal" size="15" className="mb-1 me-1" /> */}
                      <span>Regresar</span>
                    </Button>
                  </Col>
                )}
              </Row>
            </Col>
            {/* Items selector Start */}
            <Col xs="12" sm="3" className="mt-0 mt-lg-0 d-flex justify-content-end align-items-end">
              <Row className="g-0">
                <Col>
                  <Dropdown align={{ xs: 'end' }} className={`d-inline-block ms-1 ${orders?.length < 2 && 'd-none'}`}>
                    <Dropdown.Toggle variant="foreground-alternate" className="shadow sw-18">
                      {`${itemsPerPageView} Pedidos`}
                    </Dropdown.Toggle>
                    <Dropdown.Menu className="shadow dropdown-menu-end">
                      <Dropdown.Item className={`${itemsPerPageView === 10 && 'd-none'}`} onClick={() => settingPage(10)}>
                        10 Pedidos
                      </Dropdown.Item>
                      <Dropdown.Item className={`${itemsPerPageView === 20 && 'd-none'}`} onClick={() => settingPage(20)}>
                        20 Pedidos
                      </Dropdown.Item>
                      <Dropdown.Item className={`${itemsPerPageView === 50 && 'd-none'}`} onClick={() => settingPage(50)}>
                        50 Pedidos
                      </Dropdown.Item>
                      <Dropdown.Item className={`${itemsPerPageView === 100 && 'd-none'}`} onClick={() => settingPage(100)}>
                        100 Pedidos
                      </Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>
                </Col>
              </Row>
            </Col>
            {/* Items selector End */}
          </Row>
          {/* Elements for Filtering End */}
          <hr className="bg-muted mb-3 mt-3 mt-xl-4" />
          {/* List Header Start */}
          {filteredSuccess && <div className="text-muted mb-3">{filteredSuccess}</div>}
          <div className="d-flex justify-content-between mb-3 text-muted ">
            <span className={`${hasConnectionIssue ? 'invisible' : 'visible'}`}>
              Total: <strong>{fm.from(totalItems)}</strong> pedidos
            </span>
            <span className={`d-block ${orders?.length < 2 && 'd-none'}`}>
              Página <strong>{pageView}</strong>
            </span>
          </div>
          <ProvisionalUserAlert title="Pedidos" />
          <Row className={`g-0 mb-2 d-none d-lg-flex ${orders?.length === 0 && 'invisible'} `}>
            <Col lg="auto" className="sw-12 bg-warningx" />
            <Col className="pe-4 ">
              <Row className="g-0 h-100 align-content-center custom-sort h-100 bg-primaryx">
                <Col lg="1" className="d-flex flex-column mb-lg-0 ms-3 pe-3 d-flex bg-infox">
                  <div className="text-muted text-small cursor-pointer">ID</div>
                </Col>
                <Col lg="4" className="d-flex flex-column pe-1 justify-content-center bg-secondaryx">
                  <div className="text-muted text-small cursor-pointer">DIRECCIÓN</div>
                </Col>
                <Col lg="1" className="d-flex flex-column align-items-center mb-lg-0 pe-3 d-flex bg-infox">
                  <div className="text-muted text-small cursor-pointer">FECHA</div>
                </Col>

                <Col lg="2" className="d-flex flex-column pe-1 justify-content-center align-items-center bg-alternatex">
                  <div className="text-muted text-small cursor-pointer">COMPRA</div>
                </Col>
                <Col lg="2" className="d-flex flex-column pe-1 justify-content-center align-items-center bg-infox" />
              </Row>
            </Col>
          </Row>
          {/* List Header End */}
          {/* List shipment Items End */}
          {orders?.length === 0 && hasConnectionIssue && <ConnectionError refreshBtn />}
          {emptyHistory && (
            <EmptyPage
              text="Integra tu tienda con uno de nuestros plugins y empieza a gestionar tus pedidos desde aquí."
              btnText="Conoce cómo integrar tu tienda"
              path="/integraciones/documentacion"
              isCallToAction
              showAnimation
            />
          )}
          {orders?.length === 0 && filteredSuccess && <EmptyFilterResult />}
          {orders?.length > 0 && (
            <>
              {/* List shipment Items Start */}
              {orders?.map((order) => (
                <Card className="mb-2 d-flex" key={order?.id}>
                  <Row className="g-0 h-100 sh-lg-9 position-relative">
                    <Col
                      xs="auto"
                      className="bg-backgroundx rounded-top-start rounded-bottom-start overflow-hidden d-flex justify-content-center align-items-center"
                    >
                      <div className="h-100 sh-lg-9 d-flex justify-content-center align-items-center bg-infox" style={{ width: '100px' }}>
                        <img src={order?.store_pic} alt="tienda" className="bg-successx w-100" />
                      </div>
                    </Col>
                    <Col className="py-4 ps-3 pe-3 pe-lg-4 py-lg-0 h-100 bg-infox">
                      <Row className="g-0 h-100 align-content-center">
                        <Col xs="12" lg="1" className="d-flex flex-column mb-lg-0 mb-3 pe-3 h-lg-100 justify-content-center order-2 xbg-warningx">
                          <div className="text-muted text-small d-lg-none mb-1">ID Pedido / Tienda </div>
                          <OverlayTrigger delay={{ show: 1000, hide: 0 }} placement="top" overlay={<Tooltip id="tooltip-top">Ver detalle de pedido</Tooltip>}>
                            <NavLink className="anchor-effect-primary" to={`/integraciones/pedidos/${order?.id}`}>
                              {order?.order_key}
                            </NavLink>
                          </OverlayTrigger>
                          <div className="text-medium text-muted">{order?.store_ecommerce_name}</div>
                        </Col>
                        <Col xs="12" lg="4" className="d-flex flex-column justify-content-center pe-5 mb-2 mb-lg-0 order-3 order-lg-2 bg-secondaryX">
                          <div className="text-muted text-small d-lg-none mb-1">Dirección</div>
                          <div className="text-truncate">{order?.address_1}</div>
                          <div className={`text-medium text-muted text-truncate ${order?.zip_code ? 'visible' : 'invisible'}`}>{`C.P. ${order?.zip_code}`}</div>
                        </Col>
                        <Col
                          xs="6"
                          lg="1"
                          className="d-flex flex-column justify-content-center align-items-center align-items-sm-end align-items-lg-center mb-lg-0 mb-3 pe-3 order-4 h-lg-100 bg-warningX"
                        >
                          <div className="text-muted text-small d-lg-none mb-1">Fecha</div>
                          <div className="text-muted">{moment(order?.created).format('DD-MM-YY')}</div>
                        </Col>
                        <Col
                          xs="6"
                          lg="2"
                          className="d-flex flex-column justify-content-center align-items-end align-items-lg-center pe-1 mb-2 mb-lg-0 order-5 bg-successx"
                        >
                          <div className="text-muted text-small d-lg-none mb-1">Compra</div>
                          <div className="text-center text-primary">
                            <span className="text-small text-muted">$</span>
                            {formatMoney(order?.total_order)}
                          </div>
                        </Col>
                        <Col
                          xs="12"
                          lg="2"
                          className="d-flex justify-content-end justify-content-lg-start justify-content-xl-center
                           align-items-center mb-2 mb-lg-0 order-1 order-lg-5 bg-infox"
                        >
                          <Badge bg={getStatusColor(order?.readable_internal_status)} className="text-uppercase fw-bolder" style={{ fontSize: '12px' }}>
                            {order?.readable_internal_status}
                          </Badge>
                        </Col>
                        <Col xs="12" lg="2" className="d-flex justify-content-end align-items-center my-2 my-lg-0 order-last bg-separate">
                          {(order?.readable_internal_status === 'Pendiente' || order?.readable_internal_status === 'Incompleta') && (
                            <NavLink className="btn btn-icon btn-primary text-decoration-none" to={`/envios/enviar/pedido/${order?.id}`}>
                              <span>Crear envío</span>
                              <CsLineIcons icon="chevron-right" />
                            </NavLink>
                          )}
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                </Card>
              ))}
              {/* List shipment Items End */}
              {/* Pagination Start */}
              <div className={`d-flex flex-column justify-content-center align-items-center mt-5 mx-auto ${totalPages < 2 && 'd-none'}`}>
                <Paginate currentPage={page} totalPages={totalPages} setPageReducer={setPage} />
                {/* se elimino el prop setIsLoaging */}
              </div>
              {/* Pagination End */}
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default PluginOrders;
// onClick={(e) => nextPage(e.currentTarget)}
